/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/shared/AccountDrawer.tsx
 * PURPOSE: Right slide-in account panel — deferred sections, deals table, pipeline run count, ActivityFeed, stacked z-index above ContactDrawer
 * DEPENDENCIES: react, react-dom, react-router-dom, focus-trap-react, @heroicons/react/24/outline, @tanstack/react-query, tailwind-merge, lucide-react, @shared/lib/frontend/api/accountApi, ../../context/CrmContext, ../../data/opportunityDrawerOptions, ../../data/scheduleModalCatalog, ../../hooks/useAccountDrawer, ../../types/opportunity.types, ../../utils/crmFormatters, ../../utils/pipelineRunTerritoryStore, ../activity/ActivityFeed, ./EmptyState, ./InlineEditableField, ./OwnerAvatar, ./RecordTable, ./StatusBadge
 * PUBLIC_API: AccountDrawer, AccountDrawerProps
 * SIDE_EFFECTS: Portal, body scroll lock, focus trap, patchAccount, document.title while open, openActivityModal, openOpportunityDrawer, openDrilldown
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 925/960
 * RECENT: 2026-05-25 [2.2.1197] — lazy **`AccountLinkedPagesPanel`** (linked workspace pages + brain summary).
 * RECENT_PRIOR: 2026-05-25 [2.2.1196] — lazy **`CrmDrawerBrainStrip`** from **`domain/pages/frontend/components/CrmDrawerBrainStrip`** (account insights).
 * RECENT_PRIOR: 2026-05-22 — defer FocusTrap activation (double rAF) and ignore spurious onDeactivate within 200ms of open — fixes territory account link flash-close (parity with DrilldownModal [2.2.1133] / [2.2.963])
 * RECENT_PRIOR: 2026-05-11 Task 6 #87 — drawer-first accounts; **`↗ Open full account page`** → **`/companies/:id`**
 * RECENT_PRIOR: 2026-05-11 Task 11 #92 — footer AI embed slot removed (dock). RECENT_PRIOR: 2026-05-08 [2.2.893] — root **`CHANGELOG` [2.2.893]`**
 * RECENT_PRIOR: 2026-05-08 [2.2.876] CRM persistence UX compliance: persisted CRM create, edit, task, activity, deal, account, related-entity, and pipeline run-history flows; stale demo copy removed — root **`CHANGELOG` [2.2.876]`**
 * RECENT_PRIOR: 2026-05-01 Create sentinel — skip contacts query for **`CRM_NEW_ACCOUNT_ID`**; root **`z-[110]`** above opportunity drawer — CHANGELOG [2.2.780]
 * RECENT_PRIOR: 2026-04-30 [2.2.752] Mobile sheet handle, sticky footer wrapper, section actions min touch height — CHANGELOG [2.2.752]
 * RECENT_PRIOR: 2026-04-30 [2.2.742] Right drawer z-60 stack, Vitest + Playwright parity, docs sync — CHANGELOG [2.2.742]
 */

import { ChevronDownIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { FocusTrap } from 'focus-trap-react';
import { Briefcase } from 'lucide-react';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { shellDrawerBelowTopBarStyle } from '../../../../layout/shellDrawerLayout';
import { buildCrmAccountDetailPath } from '../../../../routes/builders';
import { AccountMergeModal } from './AccountMergeModal';
import { AccountCustomerHealthBadge } from '../accounts/AccountCustomerHealthBadge';
import { AccountContractsSection } from '../accounts/AccountContractsSection';
import { AccountHierarchySection } from '../accounts/AccountHierarchySection';
import { AccountProductsSection } from '../accounts/AccountProductsSection';
import { accountApi } from '@shared/lib/frontend/api/accountApi';
import { useToast } from '@design-system/components';
import { ActivityFeed } from '../activity/ActivityFeed';
import { useCrm, useCrmBootstrap } from '../../context/CrmContext';
import { useCrmActions } from '../../context/CrmActionsContext';
import { useTenantCurrency } from '../../hooks/useTenantCurrency';
import { useCrmTerritoryOptions } from '../../hooks/useCrmTerritoryOptions';
import { useCrmUnifiedTimeline } from '../../hooks/useCrmUnifiedTimeline';
import { MOCK_SCHEDULE_OWNERS } from '../../data/scheduleModalCatalog';
import {
  CRM_NEW_ACCOUNT_ID,
  useAccountDrawer,
  type AccountDrawerModel,
  type AccountDrawerPatch,
} from '../../hooks/useAccountDrawer';
import type { Opportunity, OpportunityStage } from '../../types/opportunity.types';
import { ACCOUNT_ASSISTANT_PROMPTS, buildAccountAssistantPrefill } from '../../utils/crmAssistantPrefill';
import { formatCrmDate } from '../../utils/crmFormatters';
import { formatCrmCurrency } from '../../utils/formatCurrency';
import {
  countPipelineRunsForTerritory,
  subscribePipelineRunMemory,
} from '../../utils/pipelineRunTerritoryStore';
import { CrmAskAiIconButton } from './CrmAskAiIconButton';
import {
  CrmAutomateInBrowserButton,
  buildCrmLinkedInTask,
  buildCrmTwitterTask,
} from '@platform/browser/frontend';
import { EmptyState } from './EmptyState';
import { InlineEditableField } from './InlineEditableField';
import { OwnerAvatar } from './OwnerAvatar';
import { RecordTable, type RecordTableColumn } from './RecordTable';
import { StatusBadge } from './StatusBadge';

const OWNER_SELECT_OPTIONS = MOCK_SCHEDULE_OWNERS.map((o) => ({ value: o.id, label: o.name }));

const ACCOUNT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'partner', label: 'Partner' },
];

export type AccountDrawerProps = {
  isOpen: boolean;
  accountId: string | null;
  onClose: () => void;
  onSave: (id: string, field: string, value: unknown) => Promise<void>;
};

function isOpenDealStage(stage: OpportunityStage): boolean {
  return stage !== 'closed_won' && stage !== 'closed_lost';
}

function CollapsibleSection({ title, children }: { title: string; children: ReactNode }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="border-b border-gray-200 py-4 last:border-b-0 dark:border-gray-700">
      <button
        type="button"
        className="mb-3 flex w-full items-center justify-between gap-2 rounded-md text-left focus-visible:outline focus-visible:ring-2 focus-visible:ring-primary-500"
        aria-expanded={expanded}
        onClick={() => setExpanded((e) => !e)}
      >
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</h3>
        {expanded ? (
          <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
        ) : (
          <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
        )}
      </button>
      {expanded ? <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-x-6">{children}</div> : null}
    </div>
  );
}

function SectionChrome({
  sectionId,
  dirty,
  errorText,
  onSave,
  onCancel,
  children,
}: {
  sectionId: string;
  dirty: boolean;
  errorText: string | null;
  onSave: () => void | Promise<void>;
  onCancel: () => void;
  children: ReactNode;
}) {
  return (
    <section
      className={twMerge(
        'rounded-lg border border-transparent px-1 py-0',
        dirty && 'border-l-2 border-amber-400 pl-2'
      )}
      data-testid={`account-section-wrap-${sectionId}`}
      data-dirty={dirty ? 'true' : 'false'}
    >
      {children}
      {errorText ? (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400" role="alert">
          {errorText}
        </p>
      ) : null}
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          data-testid={`account-section-save-${sectionId}`}
          disabled={!dirty}
          className="min-h-[44px] rounded-lg bg-primary-600 px-4 py-2 text-xs font-medium text-white transition-transform hover:bg-primary-700 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
          onClick={() => void onSave()}
        >
          Save
        </button>
        <button
          type="button"
          data-testid={`account-section-cancel-${sectionId}`}
          disabled={!dirty}
          className="min-h-[44px] rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-700 transition-transform hover:bg-gray-50 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </section>
  );
}

function AccountDrawerSkeleton() {
  return (
    <div className="space-y-6" data-testid="account-drawer-skeleton" aria-busy="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="animate-pulse space-y-3">
          <div className="h-3 w-24 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="h-14 rounded-lg bg-gray-100 dark:bg-gray-800" />
            <div className="h-14 rounded-lg bg-gray-100 dark:bg-gray-800" />
          </div>
        </div>
      ))}
    </div>
  );
}

function CompanyInitialsChip({ name }: { name: string }) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const a = (parts[0]?.charAt(0) ?? '?').toUpperCase();
  const b = (parts.length > 1 ? parts[1]!.charAt(0) : parts[0]?.charAt(1) ?? '').toUpperCase();
  const text = b ? `${a}${b}` : a;
  return (
    <span
      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-800 dark:bg-primary-900/40 dark:text-primary-200"
      aria-hidden
    >
      {text}
    </span>
  );
}

function websiteHref(url: string): string {
  const t = url.trim();
  if (!t) return '#';
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t.replace(/^\/+/, '')}`;
}

const noopPersist = async () => {
  /* Deferred fields: section-level via patchAccount. */
};

const SLIDE_MS = 280;
const SLIDE_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

const CrmDrawerBrainStrip = lazy(() =>
  import('@domain/pages/frontend/components/CrmDrawerBrainStrip').then((m) => ({
    default: m.CrmDrawerBrainStrip,
  })),
);

const AccountLinkedPagesPanel = lazy(() =>
  import('@domain/pages/frontend/components/AccountLinkedPagesPanel').then((m) => ({
    default: m.AccountLinkedPagesPanel,
  })),
);

export function AccountDrawer({ isOpen, accountId, onClose, onSave }: AccountDrawerProps) {
  const {
    openActivityModal,
    openDrilldown,
    openOpportunityDrawer,
    openCrmAssistant,
    crmActivities,
    crmOpportunities,
    crmContacts,
    crm,
    refetchAllCrmLists,
  } = useCrm();
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const { pathname } = useLocation();
  const { activitiesLoading } = useCrmBootstrap();
  const { currency, locale } = useTenantCurrency();
  const titleId = useId();
  const [mounted, setMounted] = useState(isOpen);
  const [entered, setEntered] = useState(false);
  const [latchedAccount, setLatchedAccount] = useState<AccountDrawerModel | null>(null);
  const lastOpenedAccountIdRef = useRef<string | null>(null);
  const panelRef = useRef<HTMLElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const prevOpenRef = useRef(false);
  const backdropGuardRef = useRef(0);
  /** Ignore focus-trap teardown that fires in the same tick as territory account row or name activation. */
  const openedAtRef = useRef(0);
  const [focusTrapActive, setFocusTrapActive] = useState(false);

  const [pendingSummary, setPendingSummary] = useState<AccountDrawerPatch>({});
  const [pendingTerritory, setPendingTerritory] = useState<AccountDrawerPatch>({});
  const [pendingNotes, setPendingNotes] = useState<AccountDrawerPatch>({});
  const [resetSummary, setResetSummary] = useState(0);
  const [resetTerritory, setResetTerritory] = useState(0);
  const [resetNotes, setResetNotes] = useState(0);
  const [sectionErrors, setSectionErrors] = useState<Record<string, string | null>>({});
  const [pipelineTick, setPipelineTick] = useState(0);

  useEffect(() => {
    if (isOpen && accountId) lastOpenedAccountIdRef.current = accountId;
  }, [isOpen, accountId]);

  const resolvedAccountId = useMemo(() => {
    if (!mounted) return null;
    if (isOpen) return accountId;
    return lastOpenedAccountIdRef.current;
  }, [mounted, isOpen, accountId]);

  const { account, loading, error, patchAccount, reloadAccount } = useAccountDrawer(resolvedAccountId, onSave);
  const actions = useCrmActions();
  const { showToast } = useToast();
  const [lifecycleBusy, setLifecycleBusy] = useState(false);

  useEffect(() => {
    if (account) setLatchedAccount(account);
  }, [account]);
  useEffect(() => {
    if (!mounted) setLatchedAccount(null);
  }, [mounted]);

  const displayAccount = account ?? latchedAccount;

  const accountTimelineEnabled =
    mounted &&
    isOpen &&
    Boolean(displayAccount?.id) &&
    displayAccount!.id !== CRM_NEW_ACCOUNT_ID;
  const {
    activities: unifiedTimelineActivities,
    revopsLinked,
    loading: timelineLoading,
  } = useCrmUnifiedTimeline(displayAccount?.id, 'account', { enabled: accountTimelineEnabled });

  const feedActivities = useMemo(() => {
    if (unifiedTimelineActivities.length > 0 || revopsLinked) {
      return unifiedTimelineActivities;
    }
    return crmActivities;
  }, [crmActivities, revopsLinked, unifiedTimelineActivities]);

  const feedLoading = timelineLoading || activitiesLoading;

  const clearAllPending = useCallback(() => {
    setPendingSummary({});
    setPendingTerritory({});
    setPendingNotes({});
    setSectionErrors({});
    setResetSummary((x) => x + 1);
    setResetTerritory((x) => x + 1);
    setResetNotes((x) => x + 1);
  }, []);

  useEffect(() => {
    if (!isOpen) clearAllPending();
  }, [isOpen, resolvedAccountId, clearAllPending]);

  useEffect(() => subscribePipelineRunMemory(() => setPipelineTick((t) => t + 1)), []);

  const { data: contactsResp, isLoading: contactsLoading } = useQuery({
    queryKey: ['crm-account-drawer-contacts', resolvedAccountId],
    queryFn: () => accountApi.getContacts(resolvedAccountId!, { per_page: 50 }),
    enabled: Boolean(mounted && resolvedAccountId && resolvedAccountId !== CRM_NEW_ACCOUNT_ID),
  });

  const miniContacts = useMemo(() => {
    if (!displayAccount) return [];
    const fromApi = contactsResp?.contacts;
    if (Array.isArray(fromApi) && fromApi.length > 0) {
      return fromApi.slice(0, 5).map((c) => ({
        id: c.id,
        name: [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email || 'Contact',
      }));
    }
    const accountName = displayAccount.name.trim().toLowerCase();
    return crmContacts
      .filter((c) => (c.company ?? '').trim().toLowerCase() === accountName)
      .slice(0, 5)
      .map((c) => ({
        id: c.id,
        name: [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email || 'Contact',
      }));
  }, [contactsResp?.contacts, crmContacts, displayAccount]);

  const totalContactsCount = useMemo(() => {
    if (!displayAccount) return 0;
    const fromApi = contactsResp?.contacts?.length;
    if (typeof fromApi === 'number' && fromApi > 0) return fromApi;
    const accountName = displayAccount.name.trim().toLowerCase();
    return crmContacts.filter((c) => (c.company ?? '').trim().toLowerCase() === accountName).length;
  }, [contactsResp?.contacts?.length, crmContacts, displayAccount]);

  const { options: territoryOptions, territoryLabel } = useCrmTerritoryOptions();

  const openDeals = useMemo(() => {
    if (!displayAccount) return [];
    return crmOpportunities.filter(
      (o) =>
        !crm.deletedOpportunityIds.includes(o.id) &&
        isOpenDealStage(o.stage) &&
        (o.accountId === displayAccount.id ||
          o.accountName.trim().toLowerCase() === displayAccount.name.trim().toLowerCase())
    );
  }, [crmOpportunities, crm.deletedOpportunityIds, displayAccount]);

  const dealColumns: RecordTableColumn[] = useMemo(
    () => [
      { key: 'name', label: 'Deal' },
      { key: 'stage', label: 'Stage' },
      {
        key: 'amount',
        label: 'Amount',
        numeric: true,
        render: (row) => formatCrmCurrency(Number(row.amount), currency, locale),
      },
    ],
    [currency, locale]
  );

  const dealRows = useMemo(
    () =>
      openDeals.map((o) => ({
        id: o.id,
        name: o.name,
        stage: o.stage,
        amount: o.amount,
        _opp: o,
      })),
    [openDeals]
  );

  const pipelineRunCount = useMemo(() => {
    void pipelineTick;
    if (!displayAccount) return 0;
    return countPipelineRunsForTerritory(displayAccount.territoryId);
  }, [displayAccount, pipelineTick]);

  const handleFocusTrapDeactivate = useCallback(() => {
    if (performance.now() - openedAtRef.current < 200) return;
    onClose();
  }, [onClose]);

  useEffect(() => {
    let outerRaf = 0;
    let innerRaf = 0;
    let t = 0;
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = isOpen;

    if (isOpen && !wasOpen) {
      setMounted(true);
      setEntered(false);
      openedAtRef.current = performance.now();
      setFocusTrapActive(false);
      outerRaf = requestAnimationFrame(() => {
        innerRaf = requestAnimationFrame(() => {
          setEntered(true);
          setFocusTrapActive(true);
        });
      });
    } else if (!isOpen && wasOpen) {
      setEntered(false);
      setFocusTrapActive(false);
      t = window.setTimeout(() => setMounted(false), SLIDE_MS);
    }
    return () => {
      cancelAnimationFrame(outerRaf);
      if (innerRaf) cancelAnimationFrame(innerRaf);
      window.clearTimeout(t);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!mounted) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mounted]);

  useEffect(() => {
    if (!isOpen || !mounted) return;
    backdropGuardRef.current = Date.now() + 400;
  }, [isOpen, mounted]);

  const displayName =
    displayAccount?.name?.trim() ||
    (displayAccount?.id === CRM_NEW_ACCOUNT_ID ? 'New account' : 'Account');

  useEffect(() => {
    if (!isOpen || !displayAccount) return;
    const prevTitle = document.title;
    document.title = `${displayName} — Accounts — Brazn`;
    return () => {
      document.title = prevTitle;
    };
  }, [isOpen, displayAccount, displayName]);

  const handleLogActivity = useCallback(() => {
    if (!displayAccount) return;
    openActivityModal({
      relatedEntityId: displayAccount.id,
      relatedEntityType: 'account',
      relatedEntityName: displayAccount.name,
      subject: `Activity — ${displayAccount.name}`,
    });
  }, [openActivityModal, displayAccount]);

  const mergeDisplay = useCallback((a: AccountDrawerModel, p: AccountDrawerPatch): AccountDrawerModel => {
    let next: AccountDrawerModel = { ...a, ...p };
    if (p.accountOwnerId) {
      const o = MOCK_SCHEDULE_OWNERS.find((x) => x.id === p.accountOwnerId);
      if (o) next = { ...next, accountOwnerName: o.name };
    }
    return next;
  }, []);

  const accountPrefill = useMemo(() => {
    if (!displayAccount) return '';
    const v = mergeDisplay(
      mergeDisplay(mergeDisplay(displayAccount, pendingSummary), pendingTerritory),
      pendingNotes,
    );
    const totalOpen = openDeals.reduce((s, o) => s + Number(o.amount), 0);
    return buildAccountAssistantPrefill(v, {
      openDealsCount: openDeals.length,
      totalOpenDealValue: totalOpen,
    });
  }, [
    displayAccount,
    mergeDisplay,
    openDeals,
    pendingNotes,
    pendingSummary,
    pendingTerritory,
  ]);

  const handleAskAi = useCallback(() => {
    if (!displayAccount) return;
    const v = mergeDisplay(
      mergeDisplay(mergeDisplay(displayAccount, pendingSummary), pendingTerritory),
      pendingNotes
    );
    const totalOpen = openDeals.reduce((s, o) => s + Number(o.amount), 0);
    openCrmAssistant({
      prefillContext: buildAccountAssistantPrefill(v, {
        openDealsCount: openDeals.length,
        totalOpenDealValue: totalOpen,
      }),
      suggestedPrompts: [...ACCOUNT_ASSISTANT_PROMPTS],
      pageContext: { path: pathname, title: displayName },
    });
  }, [
    displayAccount,
    displayName,
    mergeDisplay,
    openCrmAssistant,
    openDeals,
    pathname,
    pendingNotes,
    pendingSummary,
    pendingTerritory,
  ]);

  const handleArchiveAccount = useCallback(async () => {
    if (!displayAccount?.id || displayAccount.id === CRM_NEW_ACCOUNT_ID) return;
    setLifecycleBusy(true);
    try {
      await actions.deleteTerritoryAccount(displayAccount.id);
      await refetchAllCrmLists();
      showToast({ variant: 'success', message: 'Account archived — hidden from default lists.' });
      onClose();
    } catch {
      showToast({ variant: 'error', message: 'Could not archive this account.' });
    } finally {
      setLifecycleBusy(false);
    }
  }, [actions, displayAccount?.id, onClose, refetchAllCrmLists, showToast]);

  const handleRestoreAccount = useCallback(async () => {
    if (!displayAccount?.id || displayAccount.id === CRM_NEW_ACCOUNT_ID) return;
    setLifecycleBusy(true);
    try {
      await actions.restoreTerritoryAccount(displayAccount.id);
      await refetchAllCrmLists();
      reloadAccount();
      showToast({ variant: 'success', message: 'Account restored to active lists.' });
    } catch {
      showToast({ variant: 'error', message: 'Could not restore this account.' });
    } finally {
      setLifecycleBusy(false);
    }
  }, [actions, displayAccount?.id, refetchAllCrmLists, reloadAccount, showToast]);

  const dirtySummary = Object.keys(pendingSummary).length > 0;
  const dirtyTerritory = Object.keys(pendingTerritory).length > 0;
  const dirtyNotes = Object.keys(pendingNotes).length > 0;

  const saveSummary = useCallback(async () => {
    if (!displayAccount) return;
    const err =
      pendingSummary.name !== undefined && !String(pendingSummary.name).trim()
        ? 'Company name is required.'
        : null;
    setSectionErrors((s) => ({ ...s, summary: err }));
    if (err) return;
    try {
      await patchAccount(pendingSummary);
      setPendingSummary({});
      setResetSummary((x) => x + 1);
      setSectionErrors((s) => ({ ...s, summary: null }));
    } catch (e) {
      setSectionErrors((s) => ({
        ...s,
        summary: e instanceof Error ? e.message : 'Save failed.',
      }));
    }
  }, [displayAccount, pendingSummary, patchAccount]);

  const saveTerritory = useCallback(async () => {
    if (!displayAccount) return;
    try {
      await patchAccount(pendingTerritory);
      setPendingTerritory({});
      setResetTerritory((x) => x + 1);
      setSectionErrors((s) => ({ ...s, territory: null }));
    } catch (e) {
      setSectionErrors((s) => ({
        ...s,
        territory: e instanceof Error ? e.message : 'Save failed.',
      }));
    }
  }, [displayAccount, pendingTerritory, patchAccount]);

  const saveNotes = useCallback(async () => {
    if (!displayAccount) return;
    try {
      await patchAccount(pendingNotes);
      setPendingNotes({});
      setResetNotes((x) => x + 1);
      setSectionErrors((s) => ({ ...s, notes: null }));
    } catch (e) {
      setSectionErrors((s) => ({
        ...s,
        notes: e instanceof Error ? e.message : 'Save failed.',
      }));
    }
  }, [displayAccount, pendingNotes, patchAccount]);

  const accountDrilldownPreset = useMemo(
    () => ({ accountExact: displayAccount?.name ?? '' }),
    [displayAccount?.name]
  );

  const openContactsDrilldown = useCallback(() => {
    if (!displayAccount) return;
    openDrilldown({
      title: `Contacts — ${displayAccount.name}`,
      subtitle: displayAccount.name,
      entityType: 'contacts',
      filterPreset: accountDrilldownPreset,
    });
  }, [displayAccount, openDrilldown, accountDrilldownPreset]);

  const nextScheduledForAccount = useMemo(() => {
    if (!displayAccount) return null;
    const rows = crmActivities.filter(
      (a) =>
        a.relatedEntityType === 'account' &&
        a.relatedEntityId === displayAccount.id &&
        a.status === 'scheduled'
    );
    if (rows.length === 0) return null;
    return [...rows].sort((a, b) => a.dueAtISO.localeCompare(b.dueAtISO))[0] ?? null;
  }, [crmActivities, displayAccount]);

  if (!mounted || typeof document === 'undefined') {
    return null;
  }

  const slideTransform = entered ? 'translateX(0)' : 'translateX(100%)';
  const panelWidthClass =
    'w-screen max-w-[100vw] md:w-[clamp(420px,35vw,560px)] md:max-w-[min(560px,100%)]';

  const bodyContent = (() => {
    if (!resolvedAccountId) {
      return (
        <p className="text-sm text-gray-600 dark:text-gray-400" role="status">
          No account selected.
        </p>
      );
    }
    if (loading && !displayAccount) {
      return <AccountDrawerSkeleton />;
    }
    if (!displayAccount) {
      return (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {error?.message ?? 'Unable to load this account.'}
        </p>
      );
    }

    const vSummary = mergeDisplay(displayAccount, pendingSummary);
    const vTerritory = mergeDisplay(displayAccount, pendingTerritory);
    const vNotes = mergeDisplay(displayAccount, pendingNotes);
    const web = vSummary.website.trim();
    const webHref = websiteHref(web);
    const isArchivedAccount = displayAccount.isArchived;

    return (
      <div className="space-y-1 pb-6">
        {isArchivedAccount ? (
          <div
            className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-100"
            role="status"
            data-testid="account-drawer-archived-banner"
          >
            This account is archived and hidden from default lists. Restore it to edit fields or log new activity.
          </div>
        ) : null}
        {displayAccount.id !== CRM_NEW_ACCOUNT_ID && !isArchivedAccount ? (
          <Suspense
            fallback={
              <div
                className="mb-4 h-14 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800"
                data-testid="account-drawer-brain-fallback"
                aria-hidden
              />
            }
          >
            <CrmDrawerBrainStrip
              focusKind="account"
              entityId={displayAccount.id}
              entityLabel={vSummary.name}
              enabled={isOpen}
            />
          </Suspense>
        ) : null}
        {displayAccount.id !== CRM_NEW_ACCOUNT_ID && !isArchivedAccount ? (
          <Suspense
            fallback={
              <div
                className="border-b border-gray-200 py-4 dark:border-gray-700"
                data-testid="account-linked-pages-lazy-fallback"
              >
                <div className="h-12 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
              </div>
            }
          >
            <AccountLinkedPagesPanel
              accountId={displayAccount.id}
              accountLabel={vSummary.name}
            />
          </Suspense>
        ) : null}
        <SectionChrome
          sectionId="summary"
          dirty={dirtySummary && !isArchivedAccount}
          errorText={sectionErrors.summary ?? null}
          onSave={saveSummary}
          onCancel={() => {
            setPendingSummary({});
            setResetSummary((x) => x + 1);
            setSectionErrors((s) => ({ ...s, summary: null }));
          }}
        >
          <CollapsibleSection title="Summary">
            <div className="md:col-span-2 flex flex-wrap items-center gap-3" data-testid="account-section-summary">
              <CompanyInitialsChip name={vSummary.name} />
              <div className="min-w-0 flex-1 space-y-2">
                <div key={`sum-${resetSummary}`} className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-x-6 md:col-span-2">
                  <InlineEditableField
                    label="Company name"
                    fieldType="text"
                    value={vSummary.name}
                    persistence="deferred"
                    readOnly={isArchivedAccount}
                    onDeferredCommit={(v) => setPendingSummary((p) => ({ ...p, name: String(v).trim() }))}
                    onSave={noopPersist}
                  />
                  <InlineEditableField
                    label="Industry"
                    fieldType="text"
                    value={vSummary.industry}
                    persistence="deferred"
                    readOnly={isArchivedAccount}
                    onDeferredCommit={(v) => setPendingSummary((p) => ({ ...p, industry: String(v).trim() }))}
                    onSave={noopPersist}
                  />
                  <InlineEditableField
                    label="Website"
                    fieldType="text"
                    value={vSummary.website}
                    persistence="deferred"
                    readOnly={isArchivedAccount}
                    onDeferredCommit={(v) => setPendingSummary((p) => ({ ...p, website: String(v).trim() }))}
                    onSave={noopPersist}
                  />
                  <InlineEditableField
                    label="Size (employees)"
                    fieldType="number"
                    value={vSummary.employeeCount}
                    persistence="deferred"
                    readOnly={isArchivedAccount}
                    onDeferredCommit={(v) => {
                      const n = typeof v === 'number' ? v : Number(v);
                      const c = Number.isFinite(n) && n >= 0 ? Math.round(n) : 0;
                      setPendingSummary((p) => ({ ...p, employeeCount: c }));
                    }}
                    onSave={noopPersist}
                  />
                  <InlineEditableField
                    label="Status"
                    fieldType="select"
                    options={ACCOUNT_STATUS_OPTIONS}
                    value={vSummary.status}
                    persistence="deferred"
                    readOnly={isArchivedAccount}
                    onDeferredCommit={(v) => setPendingSummary((p) => ({ ...p, status: String(v).trim() }))}
                    onSave={noopPersist}
                  />
                </div>
              </div>
            </div>
            <div className="md:col-span-2 flex flex-wrap items-end gap-3">
              {web ? (
                <a
                  href={webHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
                  data-testid="account-website-external-link"
                >
                  Open website
                </a>
              ) : (
                <span className="text-xs text-gray-500 dark:text-gray-400">Add a website URL to enable the link.</span>
              )}
            </div>
          </CollapsibleSection>
        </SectionChrome>

        <SectionChrome
          sectionId="territory"
          dirty={dirtyTerritory && !isArchivedAccount}
          errorText={sectionErrors.territory ?? null}
          onSave={saveTerritory}
          onCancel={() => {
            setPendingTerritory({});
            setResetTerritory((x) => x + 1);
            setSectionErrors((s) => ({ ...s, territory: null }));
          }}
        >
          <CollapsibleSection title="Territory">
            <div className="md:col-span-2 space-y-2" data-testid="account-section-territory">
              <p className="text-sm text-gray-800 dark:text-gray-200">
                <span className="font-medium text-gray-600 dark:text-gray-400">Territory: </span>
                {territoryLabel(vTerritory.territoryId)}
              </p>
              <p className="text-sm text-gray-800 dark:text-gray-200">
                <span className="font-medium text-gray-600 dark:text-gray-400">Owner: </span>
                {vTerritory.accountOwnerName || '—'}
              </p>
              <div key={`terr-${resetTerritory}`} className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-x-6 md:col-span-2">
                <InlineEditableField
                  label="Territory"
                  fieldType="select"
                  options={territoryOptions}
                  value={vTerritory.territoryId}
                  persistence="deferred"
                  readOnly={isArchivedAccount}
                  onDeferredCommit={(v) => setPendingTerritory((p) => ({ ...p, territoryId: String(v) }))}
                  onSave={noopPersist}
                />
                <InlineEditableField
                  label="Account owner"
                  fieldType="select"
                  options={OWNER_SELECT_OPTIONS}
                  value={vTerritory.accountOwnerId}
                  persistence="deferred"
                  readOnly={isArchivedAccount}
                  onDeferredCommit={(v) => setPendingTerritory((p) => ({ ...p, accountOwnerId: String(v) }))}
                  onSave={noopPersist}
                />
              </div>
            </div>
          </CollapsibleSection>
        </SectionChrome>

        {displayAccount.salesAccountId && displayAccount.id !== CRM_NEW_ACCOUNT_ID ? (
          <AccountHierarchySection
            salesAccountId={displayAccount.salesAccountId}
            readOnly={isArchivedAccount}
            onNavigateAway={onClose}
          />
        ) : null}

        <CollapsibleSection title="Contacts">
          <div className="md:col-span-2 space-y-3" data-testid="account-section-contacts">
            <div className="flex flex-wrap items-center gap-2">
              {miniContacts.map((c) => (
                <OwnerAvatar key={c.id} name={c.name} size="sm" showName />
              ))}
              {miniContacts.length === 0 ? (
                <span className="text-sm text-gray-600 dark:text-gray-400">No linked contacts yet.</span>
              ) : null}
            </div>
            <button
              type="button"
              className="text-sm font-semibold text-primary-600 hover:underline dark:text-primary-400"
              data-testid="account-contacts-view-all"
              onClick={openContactsDrilldown}
            >
              View all{totalContactsCount > 0 ? ` (${totalContactsCount})` : ''}
            </button>
            {contactsLoading ? <span className="text-xs text-gray-500">Loading contacts…</span> : null}
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Deals">
          <div className="md:col-span-2" data-testid="account-section-deals">
            <RecordTable
              caption="Open deals for this account"
              columns={dealColumns}
              data={dealRows}
              empty={
                <EmptyState
                  title="No open deals"
                  description="Won or lost stages are hidden; opportunities linked to this account appear here."
                  icon={<Briefcase className="h-10 w-10 text-gray-300 dark:text-gray-600" aria-hidden />}
                />
              }
              onRowClick={(row) => {
                const opp = (row as { _opp?: Opportunity })._opp;
                if (opp?.id) openOpportunityDrawer(opp.id);
              }}
            />
          </div>
        </CollapsibleSection>

        {displayAccount.id !== CRM_NEW_ACCOUNT_ID ? (
          <AccountContractsSection accountId={displayAccount.id} readOnly={isArchivedAccount} />
        ) : null}

        {displayAccount.id !== CRM_NEW_ACCOUNT_ID ? (
          <AccountProductsSection accountId={displayAccount.id} readOnly={isArchivedAccount} />
        ) : null}

        <section className="border-b border-gray-200 py-4 dark:border-gray-700" data-testid="account-section-pipeline">
          <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Pipeline
          </h3>
          <p className="text-sm text-gray-900 dark:text-white" data-testid="account-pipeline-run-count">
            <span className="font-semibold tabular-nums">{pipelineRunCount}</span>{' '}
            {`active pipeline run${pipelineRunCount === 1 ? '' : 's'} for this account's territory (${territoryLabel(displayAccount.territoryId)}).`}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Run history is stored in the Pipeline tab; this territory count updates after runs from this workspace session.
          </p>
        </section>

        <section className="border-b border-gray-200 py-4 dark:border-gray-700" data-testid="account-section-activity">
          <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Activity
          </h3>
          {revopsLinked ? (
            <p
              className="mb-3 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-900 dark:border-indigo-900 dark:bg-indigo-950/40 dark:text-indigo-100"
              data-testid="account-timeline-revops-banner"
            >
              Includes RevOps activity from integrations and call recordings (read-only).
            </p>
          ) : null}
          <div className="mb-3">
            {!isArchivedAccount ? (
              <button
                type="button"
                className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 focus-visible:outline focus-visible:ring-2 focus-visible:ring-primary-500"
                data-testid="account-drawer-log-activity"
                onClick={handleLogActivity}
              >
                Log activity
              </button>
            ) : null}
          </div>
          <ActivityFeed
            activities={feedActivities}
            loading={feedLoading}
            accountId={displayAccount.id}
            showLogButton={false}
            onLogActivity={handleLogActivity}
          />
        </section>

        <SectionChrome
          sectionId="notes"
          dirty={dirtyNotes && !isArchivedAccount}
          errorText={sectionErrors.notes ?? null}
          onSave={saveNotes}
          onCancel={() => {
            setPendingNotes({});
            setResetNotes((x) => x + 1);
            setSectionErrors((s) => ({ ...s, notes: null }));
          }}
        >
          <CollapsibleSection title="Notes">
            <div key={`notes-${resetNotes}`} className="md:col-span-2" data-testid="account-section-notes">
              <InlineEditableField
                label="Notes"
                fieldType="textarea"
                value={vNotes.notes}
                persistence="deferred"
                readOnly={isArchivedAccount}
                onDeferredCommit={(v) => setPendingNotes((p) => ({ ...p, notes: String(v).trim() }))}
                onSave={noopPersist}
              />
            </div>
          </CollapsibleSection>
        </SectionChrome>
      </div>
    );
  })();

  const footerInner = displayAccount ? (
    <div className="space-y-2 border-t border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Next scheduled activity
      </div>
      {nextScheduledForAccount ? (
        <p className="text-sm text-gray-900 dark:text-white" data-testid="account-drawer-next-activity">
          {nextScheduledForAccount.subject} — {formatCrmDate(nextScheduledForAccount.dueAtISO)}
        </p>
      ) : (
        <p className="text-sm text-gray-600 dark:text-gray-400">No upcoming activities for this account.</p>
      )}
    </div>
  ) : null;

  const headerIndustry = displayAccount ? mergeDisplay(displayAccount, pendingSummary).industry.trim() : '';

  const overlay = (
    <div
      className={twMerge('pointer-events-none fixed z-[110] flex justify-end')}
      style={shellDrawerBelowTopBarStyle}
      role="presentation"
      data-testid="account-drawer-root"
    >
      <FocusTrap
        active={mounted && focusTrapActive}
        focusTrapOptions={{
          initialFocus: () => (closeBtnRef.current ?? panelRef.current ?? document.body) as HTMLElement,
          returnFocusOnDeactivate: true,
          preventScroll: true,
          escapeDeactivates: true,
          clickOutsideDeactivates: false,
          onDeactivate: handleFocusTrapDeactivate,
          fallbackFocus: () => (panelRef.current ?? document.body) as HTMLElement,
        }}
      >
        <div className="pointer-events-auto relative flex h-full min-w-0 w-full justify-end overflow-x-hidden">
          <div
            className="absolute inset-0 bg-gray-900/40 backdrop-blur-[1px] dark:bg-black/40"
            aria-hidden
            data-testid="account-drawer-backdrop"
            onClick={(e) => {
              e.stopPropagation();
              if (Date.now() < backdropGuardRef.current) return;
              onClose();
            }}
          />
          <aside
            ref={panelRef}
            className={twMerge(
              'pointer-events-auto relative z-10 flex h-full min-w-0 flex-col overflow-x-hidden border-l border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800',
              panelWidthClass
            )}
            style={{
              transform: slideTransform,
              transition: `transform ${SLIDE_MS}ms ${SLIDE_EASE}`,
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
          >
            <div className="flex shrink-0 justify-center bg-white pt-3 dark:bg-gray-800 md:hidden">
              <div
                data-testid="crm-drawer-drag-handle"
                className="h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-600"
                aria-hidden
              />
            </div>
            <header className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
              <div className="min-w-0 flex-1">
                <h2 id={titleId} className="truncate text-lg font-semibold text-gray-900 dark:text-white">
                  {loading && !displayAccount ? 'Account' : displayName}
                </h2>
                {displayAccount ? (
                  <div className="mt-2 flex flex-col gap-2">
                    {headerIndustry ? (
                      <p className="truncate text-sm text-gray-600 dark:text-gray-400">{headerIndustry}</p>
                    ) : null}
                    <StatusBadge status={mergeDisplay(displayAccount, pendingSummary).status} size="sm" />
                    {displayAccount.isArchived ? (
                      <StatusBadge status="archived" size="sm" />
                    ) : null}
                    {displayAccount.id !== CRM_NEW_ACCOUNT_ID ? (
                      <AccountCustomerHealthBadge accountId={displayAccount.id} />
                    ) : null}
                    {displayAccount.id !== CRM_NEW_ACCOUNT_ID ? (
                      <Link
                        to={buildCrmAccountDetailPath(displayAccount.id)}
                        data-testid="account-drawer-open-full-page"
                        className="inline-flex w-fit max-w-full items-center gap-1 truncate text-xs font-medium text-primary-600 hover:underline focus-visible:outline focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-primary-400"
                        onClick={() => {
                          onClose();
                        }}
                      >
                        ↗ Open full account page
                      </Link>
                    ) : null}
                    {displayAccount.salesAccountId ? (
                      <>
                        <Link
                          to={`/accounts/${displayAccount.salesAccountId}`}
                          data-testid="account-drawer-sales-intelligence-link"
                          className="inline-flex w-fit max-w-full items-center gap-1 truncate text-xs font-medium text-indigo-700 hover:underline focus-visible:outline focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-indigo-300"
                          onClick={() => {
                            onClose();
                          }}
                        >
                          ↗ Sales Intelligence profile
                        </Link>
                        <Link
                          to={`/accounts/${displayAccount.salesAccountId}?tab=subsidiaries`}
                          data-testid="account-drawer-hierarchy-link"
                          className="inline-flex w-fit max-w-full items-center gap-1 truncate text-xs font-medium text-gray-600 hover:underline dark:text-gray-400"
                          onClick={() => {
                            onClose();
                          }}
                        >
                          ↗ Corporate hierarchy
                        </Link>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div className="flex shrink-0 items-start gap-2">
                {displayAccount && displayAccount.id !== CRM_NEW_ACCOUNT_ID ? (
                  displayAccount.isArchived ? (
                    <button
                      type="button"
                      className="rounded-lg bg-primary-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                      data-testid="account-drawer-restore"
                      disabled={lifecycleBusy}
                      onClick={() => void handleRestoreAccount()}
                    >
                      Restore
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="rounded-lg px-2 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800 disabled:opacity-50"
                      data-testid="account-drawer-archive"
                      disabled={lifecycleBusy}
                      onClick={() => void handleArchiveAccount()}
                    >
                      Archive
                    </button>
                  )
                ) : null}
                {displayAccount && displayAccount.id !== CRM_NEW_ACCOUNT_ID && !displayAccount.isArchived ? (
                  <button
                    type="button"
                    className="rounded-lg px-2 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                    data-testid="account-drawer-merge"
                    onClick={() => setMergeModalOpen(true)}
                  >
                    Merge
                  </button>
                ) : null}
                {displayAccount ? (
                  <>
                    <CrmAutomateInBrowserButton
                      mission="linkedin"
                      task={buildCrmLinkedInTask(displayName)}
                      context={accountPrefill}
                      dataTestId="account-drawer-automate-linkedin"
                      label="LinkedIn"
                    />
                    <CrmAutomateInBrowserButton
                      mission="twitter"
                      task={buildCrmTwitterTask(displayName)}
                      context={accountPrefill}
                      dataTestId="account-drawer-automate-social"
                      label="Social"
                    />
                    <CrmAskAiIconButton dataTestId="account-drawer-ask-ai" onClick={handleAskAi} />
                  </>
                ) : null}
                <button
                  ref={closeBtnRef}
                  type="button"
                  className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus-visible:outline focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:bg-gray-800 dark:hover:text-gray-100"
                  aria-label="Close"
                  data-testid="account-drawer-close"
                  onClick={onClose}
                >
                  <XMarkIcon className="h-5 w-5" aria-hidden />
                </button>
              </div>
            </header>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">{bodyContent}</div>
            {footerInner ? (
              <div className="shrink-0 border-t border-gray-200 bg-white pb-[max(0.5rem,env(safe-area-inset-bottom))] shadow-[0_-4px_12px_rgba(0,0,0,0.06)] dark:border-gray-700 dark:bg-gray-800 max-md:sticky max-md:bottom-0 md:shadow-none">
                {footerInner}
              </div>
            ) : null}
          </aside>
        </div>
      </FocusTrap>
    </div>
  );

  return (
    <>
      {createPortal(overlay, document.body)}
      {displayAccount && displayAccount.id !== CRM_NEW_ACCOUNT_ID ? (
        <AccountMergeModal
          open={mergeModalOpen}
          onClose={() => setMergeModalOpen(false)}
          survivorId={displayAccount.id}
          survivorName={displayName}
          onMerged={async () => {
            await refetchAllCrmLists();
            setMergeModalOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
